import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { comics } from "@/mocks/comics";
import SectionTitle from "@/components/feature/SectionTitle";

const sortKeyMap: Record<string, { label: string; icon: string; sort: (a: any, b: any) => number }> = {
  view: { label: "人气榜", icon: "ri-fire-line", sort: (a, b) => Number(a.views.replace("万", "")) - Number(b.views.replace("万", "")) },
  fav: { label: "收藏榜", icon: "ri-heart-3-line", sort: (a, b) => Number(a.favorites.replace("万", "")) - Number(b.favorites.replace("万", "")) },
  rate: { label: "评分榜", icon: "ri-star-line", sort: (a, b) => a.rating - b.rating },
};

const medal = ["text-primary-500", "text-accent-500", "text-secondary-500"];

export default function HotSection() {
  const navigate = useNavigate();
  const [active, setActive] = useState("view");

  const list = [...comics]
    .sort(sortKeyMap[active].sort)
    .reverse()
    .slice(0, 5);

  return (
    <section className="w-full py-14 md:py-16">
      <div className="container-x">
        <SectionTitle
          kicker="人气热榜"
          title="大家都在追的漫画"
          desc="根据真实人气、收藏与评分综合生成的热榜，帮助漫迷快速发现值得一追的好作品。"
          action={
            <button
              onClick={() => navigate("/category")}
              className="inline-flex items-center gap-1 whitespace-nowrap rounded-md border border-foreground-300 px-4 py-2 text-sm font-semibold text-foreground-800 transition-colors hover:border-primary-500 hover:text-primary-600 cursor-pointer"
            >
              更多作品<i className="ri-arrow-right-line text-xs"></i>
            </button>
          }
        />

        <div className="grid grid-cols-1 gap-8 lg:grid-cols-5">
          {/* 榜单切换 */}
          <div className="lg:col-span-2">
            <div className="flex gap-1 rounded-full bg-background-100 p-1">
              {Object.entries(sortKeyMap).map(([key, item]) => (
                <button
                  key={key}
                  onClick={() => setActive(key)}
                  className={`flex flex-1 items-center justify-center gap-1.5 rounded-full px-4 py-2 text-sm font-semibold whitespace-nowrap transition-colors cursor-pointer ${
                    active === key ? "bg-primary-500 text-background-50" : "text-foreground-700 hover:text-primary-600"
                  }`}
                >
                  <i className={item.icon}></i>
                  {item.label}
                </button>
              ))}
            </div>

            <div className="mt-4 space-y-2">
              {list.map((comic, idx) => (
                <button
                  key={comic.id}
                  onClick={() => navigate(`/comic/${comic.id}`)}
                  className="group flex w-full items-center gap-3 rounded-lg border border-background-300/60 bg-background-50 p-3 text-left transition-colors hover:border-primary-300 cursor-pointer"
                >
                  <span className={`w-8 text-center font-heading text-xl font-bold ${medal[idx] ?? "text-foreground-400"}`}>
                    {idx + 1}
                  </span>
                  <div className="h-14 w-10 shrink-0 overflow-hidden rounded-md">
                    <img src={comic.cover} alt={comic.title} className="h-full w-full object-cover" loading="lazy" />
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-heading text-sm font-bold text-foreground-950 group-hover:text-primary-600">{comic.title}</p>
                    <p className="mt-0.5 text-xs text-foreground-500">
                      {comic.tag} · {comic.status} · {comic.chapters}话
                    </p>
                  </div>
                  <span className="flex shrink-0 items-center gap-1 text-xs font-semibold text-accent-600">
                    <i className="ri-star-fill text-accent-400"></i>
                    {comic.rating}
                  </span>
                </button>
              ))}
            </div>
          </div>

          {/* 榜首展示 */}
          <div className="lg:col-span-3">
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:gap-5">
              {list.slice(0, 3).map((comic) => (
                <article
                  key={comic.id}
                  onClick={() => navigate(`/comic/${comic.id}`)}
                  className="group cursor-pointer overflow-hidden rounded-lg border border-background-300/60 bg-background-50 transition-shadow hover:shadow-md"
                >
                  <div className="relative aspect-[3/4] w-full overflow-hidden">
                    <img
                      src={comic.cover}
                      alt={`${comic.title}古风漫画`}
                      className="h-full w-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                      loading="lazy"
                    />
                    <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-primary-500 font-heading text-sm font-bold text-background-50">
                      {comic.id === list[0].id ? "冠" : comic.id === list[1].id ? "亚" : "季"}
                    </span>
                  </div>
                  <div className="p-3">
                    <h3 className="truncate font-heading text-sm font-bold text-foreground-950">{comic.title}</h3>
                    <p className="mt-1.5 line-clamp-2 text-xs text-foreground-600">{comic.intro}</p>
                  </div>
                </article>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}